import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { HtmlInput } from '@workboard/ui-okr';
import { Button } from '@workboard/wobo-ui';
import MeetingServices from '../../services/meeting.services';
import { sanitize } from '../../utils/common';
import './MeetingStructuredQuestionsSection.scss';

const ROLE_EMPLOYEE = 1;
const ROLE_REVIEWER = 2;
const ANSWERED_BY_BOTH = 3;
const ROLE_KEY = { [ROLE_EMPLOYEE]: 'employee', [ROLE_REVIEWER]: 'reviewer' };
const DONUT_RADIUS = 34;
const DONUT_CIRCUMFERENCE = 2 * Math.PI * DONUT_RADIUS;
const TIMESTAMP_FORMAT = {
  month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
};

const isAssigned = (answeredBy, role) => answeredBy === ANSWERED_BY_BOTH || answeredBy === role;
const requiredDone = (state) => {
  const mine = state?.me?.role != null ? state.progress[ROLE_KEY[state.me.role]] : null;
  return mine ? mine.requiredAnswered >= mine.required : false;
};

const textOf = (html) => (html || '').replace(/<[^>]*>|&nbsp;/g, '').trim();
const isBlank = (html) => textOf(html) === '';

const percent = (done, total) => (total ? Math.round((done / total) * 100) : 0);

const formatTimestamp = (seconds) => {
  if (!seconds) return '';
  const date = new Date(seconds * 1000);
  try {
    return date.toLocaleString(window.wbConfig?.userLocale, TIMESTAMP_FORMAT);
  } catch (e) {
    return date.toLocaleString(undefined, TIMESTAMP_FORMAT);
  }
};

const errorMessage = (error, fallback) => {
  const message = error?.response?.data?.data;
  return typeof message === 'string' && message ? message : fallback;
};

const Avatar = ({ user }) => (
  user?.picture ? <img className="mscq-avatar" src={user.picture} alt="" /> : <span className="mscq-avatar" />
);

const Donut = ({ progress }) => {
  const { t } = useTranslation();
  const total = progress.employee.total + progress.reviewer.total;
  const arc = (count) => (total ? (count / total) * DONUT_CIRCUMFERENCE : 0);
  const employeeArc = arc(progress.employee.answered);
  const reviewerArc = arc(progress.reviewer.answered);

  return (
    <svg className="mscq-donut" viewBox="0 0 88 88" width="88" height="88" role="img" aria-label={`${progress.overallPct}% ${t('complete')}`}>
      <circle cx="44" cy="44" r={DONUT_RADIUS} fill="none" stroke="#eef1f4" strokeWidth="10" />
      <circle
        cx="44"
        cy="44"
        r={DONUT_RADIUS}
        fill="none"
        stroke="#007FAD"
        strokeWidth="10"
        strokeDasharray={`${employeeArc} ${DONUT_CIRCUMFERENCE - employeeArc}`}
        transform="rotate(-90 44 44)"
      />
      <circle
        cx="44"
        cy="44"
        r={DONUT_RADIUS}
        fill="none"
        stroke="#0A1537"
        strokeWidth="10"
        strokeDasharray={`${reviewerArc} ${DONUT_CIRCUMFERENCE - reviewerArc}`}
        strokeDashoffset={-employeeArc}
        transform="rotate(-90 44 44)"
      />
      <text x="44" y="43" textAnchor="middle" className="mscq-donut-pct">{`${progress.overallPct}%`}</text>
      <text x="44" y="56" textAnchor="middle" className="mscq-donut-label">{t('complete')}</text>
    </svg>
  );
};

const ProgressBar = ({ label, stats }) => {
  const { t } = useTranslation();
  return (
    <div className="mscq-bar">
      <div className="mscq-bar-label">{label}</div>
      <div className="mscq-bar-track">
        <div className="mscq-bar-fill" style={{ width: `${percent(stats.requiredAnswered, stats.required)}%` }} />
      </div>
      <div className="mscq-bar-text">
        {t('{{done}} of {{total}} required answered', { done: stats.requiredAnswered, total: stats.required })}
      </div>
    </div>
  );
};

const AnswerHeader = ({
  user, label, answer, onEdit,
}) => {
  const { t } = useTranslation();
  const timestamp = answer?.answer ? formatTimestamp(answer.modifiedAt || answer.createdAt) : '';
  return (
    <div className="mscq-answer-header">
      <Avatar user={user} />
      <span className="mscq-answer-name">{label}</span>
      {timestamp && <span className="mscq-answer-time">{timestamp}</span>}
      {timestamp && answer.modifiedAt ? <span className="mscq-answer-time">{t('Edited')}</span> : null}
      {onEdit && (
        <button type="button" className="mscq-link" data-testid="mscq-edit-answer" onClick={onEdit}>
          {t('Edit')}
        </button>
      )}
    </div>
  );
};

const MyAnswer = ({
  question, answer, user, author, canEdit, onSave, protectRequired,
}) => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [requiredError, setRequiredError] = useState(false);
  const editorRef = useRef(null);
  const skipBlankSubmit = useRef(false);
  const hasAnswer = Boolean(answer?.answer);
  const showEditor = canEdit && (!hasAnswer || editing);

  useEffect(() => {
    if (editing) editorRef.current?.focus();
  }, [editing]);

  const handleSubmit = async (html) => {
    if (skipBlankSubmit.current && isBlank(html)) {
      skipBlankSubmit.current = false;
      return;
    }
    if (protectRequired && question.isRequired && isBlank(html)) {
      setRequiredError(true);
      setEditing(false);
      return;
    }
    setRequiredError(false);
    setSaving(true);
    const saved = await onSave(question.id, html);
    setSaving(false);
    if (saved) setEditing(false);
  };

  const startEditing = () => { setRequiredError(false); setEditing(true); };

  return (
    <div className="mscq-answer">
      <AnswerHeader
        user={author || user}
        label={author ? author.name : t('You')}
        answer={answer}
        onEdit={canEdit && hasAnswer && !editing ? startEditing : null}
      />
      {showEditor ? (
        <div className="mscq-editor">
          <HtmlInput
            ref={editorRef}
            ariaLabel={t('Your answer')}
            placeHolder={t('Type your answer')}
            baseUrl={window.wbConfig?.baseUrl || ''}
            disableMentions
            submitOnBlur
            loading={saving}
            initialValue={answer?.answer || ''}
            onSubmit={handleSubmit}
            onBlur={(html) => {
              skipBlankSubmit.current = false;
              if (hasAnswer && isBlank(html)) {
                handleSubmit('');
                skipBlankSubmit.current = true;
              } else if (editing && textOf(html) === textOf(answer?.answer)) {
                setEditing(false);
              }
            }}
            cancelOnEscape
            onCancel={() => setEditing(false)}
          />
        </div>
      ) : (
        <>
          {hasAnswer
            ? <div className="mscq-answer-body" dangerouslySetInnerHTML={{ __html: sanitize(answer.answer) }} />
            : <div className="mscq-empty">{t('Not answered')}</div>}
        </>
      )}
      {requiredError && (
        <div className="mscq-field-error" data-testid="mscq-required-error" role="alert">
          {t('This answer is required')}
        </div>
      )}
    </div>
  );
};

const OtherAnswer = ({
  answer, user, bothSubmitted,
}) => {
  const { t } = useTranslation();
  const hasAnswer = Boolean(answer?.answer);
  const author = answer?.user || user;

  if (!bothSubmitted) {
    return (
      <div className="mscq-answer">
        <div className="mscq-empty">{t("{{name}} hasn't submitted yet", { name: user?.firstName })}</div>
      </div>
    );
  }

  return (
    <div className="mscq-answer">
      <AnswerHeader user={author} label={author?.name} answer={answer} />
      {hasAnswer
        ? <div className="mscq-answer-body" dangerouslySetInnerHTML={{ __html: sanitize(answer.answer) }} />
        : <div className="mscq-empty">{t('Not answered')}</div>}
    </div>
  );
};

const MeetingStructuredQuestionsSection = ({ meetingId, locked }) => {
  const { t } = useTranslation();
  const meetingServices = useMemo(() => new MeetingServices(), []);
  const [state, setState] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [touched, setTouched] = useState(false);
  const [pendingSaves, setPendingSaves] = useState(0);
  const [error, setError] = useState('');
  const ticket = useRef(0);
  const latest = useRef(null);
  const mounted = useRef(true);
  const inFlight = useRef(new Set());

  const load = useCallback(async () => {
    ticket.current += 1;
    const { current } = ticket;
    const response = await meetingServices.getReviewAnswers(meetingId);
    const data = response?.data?.data;
    if (mounted.current && current === ticket.current && data && typeof data === 'object') {
      latest.current = data;
      setState(data);
    }
  }, [meetingId, meetingServices]);

  useEffect(() => {
    mounted.current = true;
    load();
    window.addEventListener('focus', load);
    return () => {
      mounted.current = false;
      window.removeEventListener('focus', load);
    };
  }, [load, locked]);

  const persistAnswer = useCallback(async (questionId, html) => {
    try {
      const response = await meetingServices.saveReviewAnswer(meetingId, questionId, html);
      if (!response?.data?.data?.answer) throw new Error('Invalid response');
      await load();
      return true;
    } catch (e) {
      setError(errorMessage(e, t('Unable to save your answer. Please try again.')));
      return false;
    }
  }, [load, meetingId, meetingServices, t]);

  const saveAnswer = useCallback((questionId, html) => {
    setError('');
    setTouched(true);
    setPendingSaves((count) => count + 1);
    const task = persistAnswer(questionId, html);
    inFlight.current.add(task);
    task.then(() => {
      inFlight.current.delete(task);
      setPendingSaves((count) => count - 1);
    });
    return task;
  }, [persistAnswer]);

  const submit = async () => {
    setSubmitting(true);
    setError('');
    try {
      await Promise.all([...inFlight.current]);
      if (!requiredDone(latest.current)) {
        setError(t('Answer all required questions to submit'));
        return;
      }
      const response = await meetingServices.submitReviewAnswers(meetingId);
      const data = response?.data?.data;
      if (!data?.questionnaire) throw new Error('Invalid response');
      ticket.current += 1;
      latest.current = data;
      setState(data);
    } catch (e) {
      setError(errorMessage(e, t('Unable to submit your responses. Please try again.')));
    } finally {
      setSubmitting(false);
    }
  };

  if (!state) return null;

  const {
    me, other, participants, questionnaire, answers, progress, bothSubmitted,
  } = state;
  const isParticipant = me.role != null;
  const submittedByMe = Boolean(me.submittedAt);
  const isLocked = locked || state.locked;
  const canEdit = state.canAnswer && !isLocked;
  const mine = isParticipant ? progress[ROLE_KEY[me.role]] : null;
  const canSubmit = requiredDone(state);
  const revealed = bothSubmitted;
  const hasMine = isParticipant && questionnaire.some(
    (section) => section.questions.some((q) => isAssigned(q.answeredBy, me.role))
  );
  const restrictToMine = hasMine && !submittedByMe;

  const sections = questionnaire
    .map((section) => ({
      ...section,
      questions: restrictToMine
        ? section.questions.filter((q) => isAssigned(q.answeredBy, me.role))
        : section.questions,
    }))
    .filter((section) => section.questions.length > 0);

  const answerFor = (questionId, role) => answers.find(
    (row) => row.questionId === questionId && row.role === role
  );

  const renderSlot = (question, role) => {
    const answer = answerFor(question.id, role);
    const user = participants[ROLE_KEY[role]];
    const foreign = Boolean(
      answer?.userId && answer.userId !== me.userId && !isBlank(answer.answer),
    );
    if (role === me.role) {
      return (
        <MyAnswer
          key={role}
          question={question}
          answer={answer}
          user={user}
          author={foreign ? answer.user : null}
          canEdit={canEdit && !foreign}
          protectRequired={submittedByMe}
          onSave={saveAnswer}
        />
      );
    }
    if (!revealed && !(isParticipant && submittedByMe)) return null;
    return <OtherAnswer key={role} answer={answer} user={user} bothSubmitted={revealed} />;
  };

  const allBars = isParticipant
    ? [
      { key: 'me', label: t('You'), stats: mine },
      { key: 'other', label: other.name, stats: progress[ROLE_KEY[other.role]] },
    ]
    : [
      { key: 'employee', label: participants.employee.name, stats: progress.employee },
      { key: 'reviewer', label: participants.reviewer.name, stats: progress.reviewer },
    ];
  const bars = allBars.filter((bar) => bar.stats && bar.stats.total > 0);

  return (
    <div className="mscq-card">
      <div className="mscq-header">
        <div className="mscq-header-main">
          <h3 className="mscq-title">{t('Structured Questions')}</h3>
          {isParticipant && (
            <p className="mscq-intro">
              {submittedByMe
                ? t('Review the answers together and agree on what to focus on next.')
                : t("Responses are submitted independently. {{name}}'s responses become visible once you submit yours, and your responses are shared with {{name}} once they submit theirs.", { name: other.firstName })}
            </p>
          )}
        </div>
        <Donut progress={progress} />
        <div className="mscq-bars">
          {bars.map((bar) => <ProgressBar key={bar.key} label={bar.label} stats={bar.stats} />)}
        </div>
        <button
          type="button"
          className={`mscq-chevron${collapsed ? ' mscq-chevron--collapsed' : ''}`}
          aria-expanded={!collapsed}
          aria-label={t('Structured Questions')}
          onClick={() => setCollapsed((value) => !value)}
        />
      </div>
      {!collapsed && (
        <>
          {isParticipant && submittedByMe && (
            <div className="mscq-banner">
              {bothSubmitted
                ? t('You and {{name}} have both submitted responses.', { name: other.firstName })
                : t("Your responses are submitted. {{name}}'s responses will appear here once they submit.", { name: other.firstName })}
            </div>
          )}
          {error && <div className="mscq-error" role="alert">{error}</div>}
          <div className="mscq-body">
            {sections.map((section) => (
              <div className="mscq-section" key={section.id}>
                <div className="mscq-section-title">{section.title || t('Untitled section')}</div>
                {section.questions.map((question) => (
                  <div className="mscq-question" key={question.id}>
                    <div className="mscq-question-row">
                      <div className="mscq-question-text" dangerouslySetInnerHTML={{ __html: sanitize(question.question) }} />
                      {question.isRequired
                        ? <span className="mscq-required" aria-hidden="true">*</span>
                        : <span className="mscq-optional">{t('(optional)')}</span>}
                    </div>
                    {[ROLE_EMPLOYEE, ROLE_REVIEWER]
                      .filter((role) => isAssigned(question.answeredBy, role))
                      .sort((a, b) => (a === me.role ? -1 : 0) - (b === me.role ? -1 : 0))
                      .map((role) => renderSlot(question, role))}
                  </div>
                ))}
              </div>
            ))}
          </div>
          {canEdit && hasMine && !submittedByMe && (
            <div className="mscq-footer">
              <span title={canSubmit ? undefined : t('Answer all required questions to submit')}>
                <Button
                  variant="primary"
                  data-testid="mscq-submit"
                  isDisabled={submitting || !touched || (!canSubmit && pendingSaves === 0)}
                  onClick={submit}
                >
                  {t('Submit')}
                </Button>
              </span>
              <span className="mscq-footer-note" data-testid="mscq-footer-note">
                {canSubmit && !touched
                  ? t('Answer a question to enable submit')
                  : t('Required questions must be answered before submitting.')}
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MeetingStructuredQuestionsSection;
